"use client"
import React from "react"
import { Sidebar, SidebarBody, SidebarLink } from "../ui/sidebar"
import { links, SubLink, Links } from "../../config/sidebar-links"
import { Link } from "react-router-dom"
import { motion } from "framer-motion"
import userService from "../../services/users/userService"

interface AdminLayoutProps {
    children: React.ReactNode
}

// Updated interface for SubLink to include allowedRoles
interface EnhancedSubLink extends SubLink {
    allowedRoles?: string[]
}

export const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
    const [open, setOpen] = React.useState(false)
    
    // Get current user role
    const currentRole = userService.getRoleFromToken() || ""
    
    // Filter links and sublinks based on user role
    const filteredLinks = React.useMemo(() => {
        // If no role or empty role, show no links
        if (!currentRole) {
            return []
        }
        
        return links.map(link => {
            // First check if the main link is accessible for this role
            if (link.allowedRoles && !link.allowedRoles.includes(currentRole)) {
                return null // This link is not accessible for the current role
            }
            
            // If this link has sublinks, filter them as well
            if (link.sublinks && link.sublinks.length > 0) {
                const filteredSublinks = link.sublinks.filter(sublink => {
                    // If the sublink has allowedRoles, check if the current role is included
                    const enhancedSublink = sublink as EnhancedSubLink
                    if (enhancedSublink.allowedRoles && enhancedSublink.allowedRoles.length > 0) {
                        return enhancedSublink.allowedRoles.includes(currentRole)
                    }
                    
                    // If no specific roles are defined for the sublink,
                    // inherit the parent link's permission
                    return true
                })
                
                // If no sublinks remain after filtering, don't show the parent link
                if (filteredSublinks.length === 0) {
                    return null
                }
                
                // Return the link with filtered sublinks
                return {
                    ...link,
                    sublinks: filteredSublinks
                }
            }
            
            // Return the link as is if it doesn't have sublinks
            return link
        }).filter(Boolean) as Links[] // Filter out null links and cast to Links[]
    }, [currentRole])
    
    return (
        <div className="flex h-screen bg-neutral-100 dark:bg-neutral-950 text-neutral-950 dark:text-neutral-50">
            {currentRole !== "caissier" && (
                <Sidebar open={open} setOpen={setOpen}>
                    <SidebarBody className="shadow-xl h-screen">
                        <div className="flex flex-col h-full">
                            <Logo open={open} />
                            <nav className="mt-8 flex flex-col gap-2 flex-1">
                                {filteredLinks.map((link, idx) => (
                                    <SidebarLink key={idx} link={link} />
                                ))}
                            </nav>
                        </div>
                    </SidebarBody>
                </Sidebar>
            )}
            
            <main className="flex-1 flex flex-col h-screen">
                <div className="p-8 flex-1 overflow-y-auto">{children}</div>
            </main>
        </div>
    )
}

interface LogoProps {
    open: boolean
}

const Logo: React.FC<LogoProps> = ({ open }) => {
    return (
        <Link to="/admin" className="font-bold flex items-center py-4 z-0 overflow-hidden">
            <motion.div
                animate={{
                    width: open ? "auto" : "2.6rem",
                }}
                className="flex items-center"
            >
                <span className="font-bold text-[2.6rem] text-primary">POS</span>
                <motion.span
                    animate={{
                        opacity: open ? 1 : 0,
                        width: open ? "auto" : 0,
                    }}
                    className="font-bold text-[2.6rem] text-neutral-600 dark:text-neutral-300"
                >
                    nex
                </motion.span>
            </motion.div>
        </Link>
    )
}